import config from '@acre/config'
import type { DropdownOption } from '@acre/design-system'

import { areCredentialsProvided, getCredsFromMetadata } from '../User/Credentials/Credentials.helpers'
import gallagherLogo from './assets/gallagher.png'

// Centralised config for referral partners, limited to referral partners that we have a specific flow for in the GI journey
type ReferralPartnerConfig = {
  messageKey: string
  insurerName?: string
  logoName?: string
  counterPartyId?: string
  localLogo?: string
}

export type ConfiguredReferralPartner = Extract<
  ReferralPartnerType,
  'UINSURE' | 'HIGOS' | 'BENNETT_ALEXANDER' | 'TOWERGATE' | 'GALLAGHER'
>

// For partners with a configured referral flow, we have a hardcoded list of partner details such as local logos and counterparty IDs for referrals
const GALLAGHER_ORG_IDS: Record<string, string> = {
  integration: '01fff7a8-1b63-411e-a391-805ff5c5ec3e',
  staging: '24cb13ca-91fa-49bd-b090-61f2ae2f5b87',
  production: '167503a2-8e3a-497d-b483-e9913b2eb320',
}

export const REFERRAL_PARTNER_CONFIG: Record<ConfiguredReferralPartner, ReferralPartnerConfig> = {
  UINSURE: { messageKey: 'uinsure' },
  HIGOS: { messageKey: 'higos', insurerName: 'Brown & Brown', logoName: 'Brown And Brown' },
  BENNETT_ALEXANDER: { messageKey: 'bennettAlexander', insurerName: 'Everywhen', logoName: 'Everywhen' },
  TOWERGATE: { messageKey: 'towergate' },
  GALLAGHER: {
    messageKey: 'gallagher',
    counterPartyId: GALLAGHER_ORG_IDS[config.ENV] || GALLAGHER_ORG_IDS.staging,
    localLogo: gallagherLogo,
  },
}

export const getPartnerLogoName = (partner: ConfiguredReferralPartner): string => {
  const config = REFERRAL_PARTNER_CONFIG[partner]
  return config?.logoName ?? partner
}

export const getPartnerInsurerName = (partner: ConfiguredReferralPartner): string | undefined => {
  const config = REFERRAL_PARTNER_CONFIG[partner]
  return config?.insurerName
}

export const getLogoNameForUnderwriter = (underwriter: string): string => {
  const partnerConfig = Object.values(REFERRAL_PARTNER_CONFIG).find((c) => c.insurerName === underwriter)
  return partnerConfig?.logoName ?? underwriter
}

export const getPartnerCounterPartyId = (partner: ConfiguredReferralPartner): string | undefined => {
  return REFERRAL_PARTNER_CONFIG[partner]?.counterPartyId
}

export const getPartnerLocalLogo = (partner: ConfiguredReferralPartner): string | undefined => {
  return REFERRAL_PARTNER_CONFIG[partner]?.localLogo
}

export const getReferralPartnerOptions = (
  formatMessage: (key: string) => string,
): DropdownOption<ReferralPartnerType>[] =>
  Object.entries(REFERRAL_PARTNER_CONFIG)
    .map(([value, config]) => ({
      value: value as ReferralPartnerType,
      label: formatMessage(`generalInsurance.partners.${config.messageKey}`),
    }))
    .sort((a, b) => a.label.localeCompare(b.label))

export const postRecommendationGIProductStages: GeneralInsuranceQuoteStage[] = [
  'SELECTED',
  'APPLICATION_IN_PROGRESS',
  'CURRENT',
]

export const hasCredentialsForGIProvider = (
  user: Maybe<User>,
  provider: Maybe<GeneralInsuranceProvider | 'MANUAL'>,
) => {
  if (!provider) return false

  if (provider === 'LV') {
    return true
  }
  if (provider === 'PAYMENTSHIELD' || provider === 'UINSURE') {
    const credentials = getCredsFromMetadata(user?.metadata || [], provider, false)
    return areCredentialsProvided(credentials)
  }

  return false
}

type FormattedProviderName = 'Uinsure' | 'PaymentShield' | 'Home In One' | 'LV= IFA' | 'Unknown provider'

export const getProviderName = (name: string): FormattedProviderName => {
  switch (name.toUpperCase()) {
    case 'UINSURE':
      return 'Uinsure'
    case 'PAYMENTSHIELD':
      return 'PaymentShield'
    case 'HOMEINONE':
      return 'Home In One'
    case 'LV':
      return 'LV= IFA'
    default:
      return 'Unknown provider'
  }
}

export const getProviderLink = (name: string) => {
  switch (name) {
    case 'UINSURE':
      return 'https://uinsure.co.uk/home-insurance/'
    case 'PAYMENTSHIELD':
      return 'https://www.paymentshield.co.uk/your-policy-documents/home-insurance'
    default:
      return undefined
  }
}
